// Из чего складывается расчётная цена продления (диалог продления, баннер
// «Расчетная стоимость продления»).
//
// Зачем: баннер показывает ОДНО число — totals.selected_price ответа
// POST /api/v1/price_quotes на ПРОДЛЁННЫЙ интервал, то есть ставку за единицу
// (сутки/час/неделю/месяц) по ступени НОВОЙ длительности брони. Оператор читает
// его как итог продления и не понимает, откуда оно взялось (отзыв сотрудника
// 2026-08-19). Ответ квоты уже несёт всю разбивку — здесь она превращается в
// витрину, ту же по составу, что панель «Сезоны» в карточке брони
// (BookingCard#applyQuoteResponse), плюс слои после сезонов: тариф и скидки.
//
// Важно про ставку: selected_price = rental_cost / units — СРЕДНЕВЗВЕШЕННАЯ
// цена единицы ПОСЛЕ тарифа и скидок (Pricing::Engine), а unit_price строк
// сезонов — цены ДО них. Поэтому ставка может не совпадать ни с одной строкой:
// признак mixedRates говорит витрине сказать об этом вслух, а priceNoSale
// показывает, с чего начинался каскад.
import { formatQuoteDate } from "./bookingQuote";

// Единица тарификации брони определяет подписи «N суток аренды» / «цена аренды
// за сутки». Ключи — те же, что в шапке редактирования (BookingCard).
const UNIT_LABEL_KEYS = {
  daily: { count: "bookings.bookings_days", price: "bookings.price_bookings_days" },
  hourly: { count: "bookings.bookings_hours", price: "bookings.price_bookings_hours" },
  weekly: { count: "bookings.bookings_weeks", price: "bookings.price_bookings_weeks" },
  monthly: { count: "bookings.bookings_months", price: "bookings.price_bookings_months" },
};

export function unitLabelKeys(rentalType) {
  return UNIT_LABEL_KEYS[rentalType] || UNIT_LABEL_KEYS.daily;
}

// Числа приходят из сети строками ("1620.0") и могут отсутствовать. null =
// «показывать нечего», а не 0: ноль в витрине читался бы как настоящая цена.
function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

// Деньги показываем с той же точностью, что и сам баннер (toFixed(2)), иначе
// строки таблицы («1620.0») и ставка («1620.00») выглядели бы разными числами.
function toMoney(value) {
  const num = toNumber(value);
  return num === null ? null : Number(num.toFixed(2));
}

function isPositiveCount(value) {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

// Строки аренды: сезоны и основные цены (season_id = null), плюс сегменты
// прошлых продлений по свободной цене оператора — как в панели «Сезоны»
// карточки. FT-130: prolong_base_residual — строка-остаток пин-квоты
// вырожденного бронирования; без неё видимые строки не сходились бы с
// итогом (dual-review 2026-08-20). Прочие kind (deposit, service, gift)
// к ставке отношения не имеют.
function buildRows(lineItems) {
  return lineItems
    .filter((item) => item && (item.kind === "rental_unit" || item.kind === "prolong_segment" || item.kind === "prolong_base_residual"))
    .map((item, index) => ({
      index,
      isProlong: item.kind === "prolong_segment" || item.kind === "prolong_base_residual",
      isBase: item.kind !== "prolong_segment" && item.season_id == null,
      // CTR-02 отдаёт ISO "YYYY-MM-DD", в витрине показываем "DD-MM-YYYY".
      startDate: formatQuoteDate(item.starts_on),
      endDate: formatQuoteDate(item.ends_on),
      qty: toNumber(item.qty),
      unitPrice: toMoney(item.unit_price),
    }));
}

// Скидки движка приходят отдельными строками со знаковым amount (минус —
// удешевление). Показываем одной суммой: оператору важен факт и величина,
// а не то, в каком из четырёх полей брони она записана.
function sumDiscounts(lineItems) {
  const total = lineItems
    .filter((item) => item && item.kind === "discount")
    .reduce((acc, item) => acc + (toNumber(item.amount) || 0), 0);

  return total === 0 ? null : Number(total.toFixed(2));
}

function sumTariffAdjustment(lineItems) {
  const total = lineItems
    .filter((item) => item && item.kind === "tariff_adjustment")
    .reduce((acc, item) => acc + (toNumber(item.amount) || 0), 0);

  return total === 0 ? null : Number(total.toFixed(2));
}

/**
 * Витрина расчёта для баннера продления.
 *
 * @param {Object} quote ответ POST /api/v1/price_quotes (CTR-02)
 * @param {Object} options
 * @param {number} options.unitsAdded сколько единиц вводит оператор в диалоге
 * @param {number} options.unitsBefore текущая длительность брони (из самой брони)
 * @param {boolean} options.hasBookingTariff у брони выбран тарифный план
 * @param {string} options.rentalType daily|hourly|weekly|monthly
 * @returns {Object|null} null, если объяснять нечего (нет ставки в ответе)
 */
export function buildProlongBreakdown(quote, options = {}) {
  if (!quote || typeof quote !== "object") return null;

  const totals = quote.totals || {};
  const unitPrice = toMoney(totals.selected_price);
  if (unitPrice === null) return null;

  const lineItems = Array.isArray(quote.line_items) ? quote.line_items : [];
  const rows = buildRows(lineItems);
  const units = quote.units || {};
  const meta = quote.meta || {};

  const unitsAdded = toNumber(options.unitsAdded);
  // units.days — число единиц квоты в единицах rental_type (не только суток),
  // см. BookingCard#applyQuoteResponse. Здесь это НОВАЯ длительность брони.
  const unitsAfter = toNumber(units.days);
  // «Было» берём из САМОЙ брони, а не как unitsAfter − unitsAdded: движок
  // добавляет сутки за остаток часов по правилам, которые зависят от тарифной
  // ступени и сезона (units.extra_day_reason), поэтому на новой длительности
  // правило может сработать иначе, чем на текущей, и вычитание показало бы
  // длительность, которой у брони никогда не было. Оба числа обязаны быть
  // первоисточниками: «было» — то, что показывает карточка, «станет» — то,
  // что посчитал движок.
  const unitsBefore = toNumber(options.unitsBefore);

  const priceNoSale = toMoney(totals.price_no_sale);
  // Ставка средняя, если сезонов/ступеней больше одной или если каскад
  // (тариф, скидка) увёл её от цены единственной строки. Тогда витрина обязана
  // сказать это словами — иначе «1620» не находится ни в одной строке таблицы.
  const tariffAdjustment = sumTariffAdjustment(lineItems);
  const mixedRates =
    rows.length > 1 || (rows.length === 1 && rows[0].unitPrice !== null && rows[0].unitPrice !== unitPrice);

  return {
    unitPrice,
    // Показываем «без скидки», только если каскад реально изменил ставку.
    priceNoSale: priceNoSale !== null && priceNoSale !== unitPrice ? priceNoSale : null,
    unitsAdded: isPositiveCount(unitsAdded) ? unitsAdded : null,
    unitsAfter,
    // Пара «было → станет» печатается, только если она осмысленна: оба числа
    // положительные и длительность действительно выросла. Иначе (нет данных
    // брони, вырожденный ответ) молчим — неверная пара хуже отсутствующей.
    unitsBefore:
      isPositiveCount(unitsBefore) && unitsAfter !== null && unitsAfter > unitsBefore
        ? unitsBefore
        : null,
    rows,
    mixedRates,
    discount: sumDiscounts(lineItems),
    tariffAdjustment: tariffAdjustment,
    // Тариф брони обычно уходит в квоту (ProlongDialog#bookingTariffPayload), но
    // справочник тарифов у карточки — СВОЕГО филиала (`@company.tariffs`), а
    // бронь может быть чужого (`main_company.bookings`). Тогда объекта тарифа
    // нет, и ставка посчитана без него — молчать об этом нельзя, разбивка
    // существует ровно чтобы объяснить показанное число.
    tariffMissing: !!options.tariffUnavailable,
    seasonMode: typeof meta.season_mode === "string" ? meta.season_mode : null,
    priceSource: meta.price_source === "current" || meta.price_source === "stored" ? meta.price_source : null,
    // Итог продления ПО ЭТОЙ ставке — то, что оператор ошибочно читает в
    // самом баннере (там стоит цена за единицу, а не сумма).
    addedCost: isPositiveCount(unitsAdded) ? Number((unitsAdded * unitPrice).toFixed(2)) : null,
    unitKeys: unitLabelKeys(options.rentalType),
  };
}
